import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Paper,
  Box,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from '@mui/material';
import {
  People,
  Person,
  Business,
  TrendingUp,
  Payment,
  CheckCircle,
  Schedule,
  AttachMoney,
} from '@mui/icons-material';
import StatsCard from '../components/StatsCard';
import adminService from '../services/adminService';
import { formatCurrency } from '../utils/helpers';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsData, analyticsData] = await Promise.all([
        adminService.getStats(),
        adminService.getAnalytics(),
      ]);
      
      setStats(statsData);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setError('שגיאה בטעינת נתונים. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="400px"
        sx={{ 
          mt: isMobile ? 2 : 4,
          px: isMobile ? 2 : 0 
        }}
      >
        <CircularProgress size={isMobile ? 40 : 60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container 
        maxWidth="lg" 
        sx={{ 
          mt: isMobile ? 2 : 4,
          px: isMobile ? 1 : 3 
        }}
      >
        <Alert 
          severity="error" 
          sx={{ 
            mt: 2,
            borderRadius: 2,
            fontSize: isMobile ? '0.875rem' : '1rem'
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        mt: isMobile ? 2 : 4, 
        mb: isMobile ? 2 : 4,
        px: isMobile ? 1 : 3,
      }}
    >
      <Typography 
        variant={isMobile ? "h5" : "h4"} 
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: 'primary.main',
          mb: isMobile ? 2 : 3,
          textAlign: isMobile ? 'center' : 'right'
        }}
      >
        דשבורד ניהול
      </Typography>

      {/* Main Stats Cards */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="סה״כ משתמשים"
            value={stats?.users?.total || 0}
            icon={<People />}
            color="primary"
            subtitle={`${stats?.users?.new_this_week || 0} חדשים השבוע`}
            compact={isMobile}
            onClick={() => navigate('/users')}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="מאמנים"
            value={stats?.users?.trainers || 0}
            icon={<Person />}
            color="success"
            subtitle="מאמנים רשומים"
            compact={isMobile}
            onClick={() => navigate('/users', { state: { userTypeFilter: 'trainer' } })}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="סטודיו"
            value={stats?.users?.studios || 0}
            icon={<Business />}
            color="warning"
            subtitle="סטודיו רשומים"
            compact={isMobile}
            onClick={() => navigate('/users', { state: { userTypeFilter: 'studio' } })}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="הכנסות חודשיות"
            value={formatCurrency(stats?.billing?.revenue_this_month || 0)}
            icon={<AttachMoney />}
            color="error"
            subtitle="החודש הנוכחי"
            compact={isMobile}
            onClick={() => navigate('/billing')}
          />
        </Grid>
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: isMobile ? 3 : 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="משתמשים פעילים"
            value={stats?.users?.active || 0}
            icon={<CheckCircle />}
            color="success"
            subtitle="משתמשים מאושרים"
            compact={isMobile}
            onClick={() => navigate('/users', { state: { statusFilter: 'active' } })}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="ממתינים לאישור"
            value={stats?.users?.pending || 0}
            icon={<Schedule />}
            color="warning"
            subtitle="משתמשים חדשים"
            compact={isMobile}
            onClick={() => navigate('/users', { state: { statusFilter: 'pending' } })}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="סה״כ הזמנות"
            value={stats?.bookings?.total || 0}
            icon={<TrendingUp />}
            color="info"
            subtitle={`${stats?.bookings?.this_month || 0} החודש`}
            compact={isMobile}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="מנויים פעילים"
            value={stats?.billing?.active_subscriptions || 0}
            icon={<Payment />}
            color="secondary"
            subtitle="מנויים פעילים"
            compact={isMobile}
            onClick={() => navigate('/billing')}
          />
        </Grid>
      </Grid>

      {/* Analytics Section */}
      <Grid container spacing={isMobile ? 2 : 3}>
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: isMobile ? 2 : 3,
              borderRadius: 2,
              boxShadow: theme.shadows[3],
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }}
          >
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                mb: isMobile ? 2 : 3,
                textAlign: 'center'
              }}
            >
              סיכום נתונים מפורט
            </Typography>
            
            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid item xs={12} md={4}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 1
                      }}
                    >
                      הרשמות חודשיות
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      נתונים זמינים ב-API לתצוגת גרפים מתקדמת
                    </Typography>
                    {stats?.users?.new_this_month && (
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mt: 1, 
                          color: 'success.main',
                          fontWeight: 'bold'
                        }}
                      >
                        {stats.users.new_this_month} הרשמות החודש
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'warning.main',
                        mb: 1
                      }}
                    >
                      חלוקת סטטוס משתמשים
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      {analytics?.status_distribution?.map((item, index) => (
                        <Typography 
                          key={index}
                          variant="body2" 
                          sx={{ 
                            mb: 0.5,
                            fontSize: isMobile ? '0.8rem' : '0.875rem',
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}
                        >
                          <span>{item.status}:</span>
                          <span style={{ fontWeight: 'bold' }}>{item.count}</span>
                        </Typography>
                      )) || (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                        >
                          נתונים לא זמינים
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card 
                  sx={{ 
                    height: '100%',
                    borderRadius: 2,
                    boxShadow: theme.shadows[2],
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease-in-out'
                  }}
                >
                  <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'error.main',
                        mb: 1
                      }}
                    >
                      הכנסות ותשלומים
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 0.5,
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>החודש:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatCurrency(stats?.billing?.revenue_this_month || 0)}
                        </span>
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 0.5,
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>השנה:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {formatCurrency(stats?.billing?.revenue_this_year || 0)}
                        </span>
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          display: 'flex',
                          justifyContent: 'space-between'
                        }}
                      >
                        <span>מנויים פעילים:</span>
                        <span style={{ fontWeight: 'bold' }}>
                          {stats?.billing?.active_subscriptions || 0}
                        </span>
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
